import { brandedFlowName, flowNamePart } from './flow-name';
// Data access core. The ONLY place that knows where data comes from.
// Pages use this compatibility facade while data is loaded and persisted by the domain API.
// No business data is read from bundled JSON or written to browser storage.

const TABLES = [
  'organizations', 'users', 'organization_members', 'workspaces', 'workspace_members', 'workspace_brands',
  'brands', 'projects', 'project_brands', 'project_members',
  'flows', 'flow_versions', 'nodes', 'edges', 'node_groups', 'node_group_members', 'node_types',
  'assets', 'asset_versions', 'asset_usages',
  'runs', 'flow_last_runs', 'run_stats', 'node_runs', 'provider_requests', 'providers', 'models', 'model_pricing', 'connections', 'variables',
  'favorites', 'recents', 'workers', 'queue_jobs', 'feature_flags', 'audit_events', 'usage_events',
  'creators', 'listings', 'gallery_items', 'purchases', 'reviews',
] as const;

/** The demo tables have heterogeneous JSON schemas; id and table names are checked,
 * while provider configs and per-asset metadata remain schema-dependent. */
export interface SeedRow { id?: string; [column: string]: any }
export type TableName = typeof TABLES[number];
const rows = Object.fromEntries(TABLES.map((table): [TableName, SeedRow[]] => [table, []])) as Record<TableName, SeedRow[]>;
const index = Object.fromEntries(TABLES.map(table => [table, new Map<string, SeedRow>()])) as Record<TableName, Map<string, SeedRow>>;
const loaded = new Set<TableName>();
const loading = new Map<TableName, Promise<void>>();
const loadErrors = new Map<TableName, string>();
export const tableLoadState = (table: TableName) => ({ ready: loaded.has(table), error: loadErrors.get(table) });
// Tables too large to load whole. They hold only the rows that paged lists (paged.ts)
// or single reads returned; byId asks the server for a row it has not seen yet.
const PAGED = new Set<TableName>(['assets']);
const rowReads = new Map<string, Promise<void> | null>();
export async function loadTable(table: TableName): Promise<void> {
  if (loaded.has(table)) return;
  if (loading.has(table)) return loading.get(table);
  loadErrors.delete(table);
  // Per-node runs belonged to the removed prototype schema. Current Canvas
  // executions keep their actual outputs in executions.node_outputs.
  if (table === 'node_runs' || PAGED.has(table)) { loaded.add(table); return; }
  const operation = (async () => {
    const pageSize = 100;
    const collected: SeedRow[] = [];
    for (let offset = 0; ; offset += pageSize) {
      const response = await fetch(`/api/v1/ui/${table}?limit=${pageSize}&offset=${offset}`);
      if (!response.ok) throw new Error('Cannot load ' + table + ' (' + response.status + ')');
      const value: unknown = await response.json();
      if (!Array.isArray(value)) throw new Error('Invalid table: ' + table);
      collected.push(...value as SeedRow[]);
      if (value.length < pageSize) break;
    }
    rows[table] = collected;
    index[table] = new Map(rows[table].filter(row => row.id).map(row => [row.id, row]));
    loaded.add(table);
    window.dispatchEvent(new CustomEvent('cf:data-updated', { detail: table }));
  })().catch(error => {
    loadErrors.set(table, error.message);
    window.dispatchEvent(new CustomEvent('cf:data-error', { detail: error.message }));
    throw error;
  }).finally(() => loading.delete(table));
  loading.set(table, operation);
  return operation;
}
export const isLoaded = (table: TableName) => loaded.has(table) || loading.has(table);
export async function refreshTable(table: TableName): Promise<void> {
  // A completion event can arrive while an earlier list request is in flight.
  // Wait for that snapshot before requesting the newer one, or the event is lost.
  const pending = loading.get(table);
  if (pending) await pending.catch((): void => undefined);
  loaded.delete(table);
  await loadTable(table);
}
// Display names follow the current project brand; stored names remain editable suffixes.
const flowViews = new WeakMap<SeedRow, SeedRow>();
export function flowBrand(flow: SeedRow): (SeedRow & { name?: string; slug?: string }) | null {
  void loadTable('projects').catch((): void => undefined);
  void loadTable('brands').catch((): void => undefined);
  const project = index.projects.get(flow.project_id);
  const brandId = project?.brand_id || project?.data?.brand_id;
  return brandId ? index.brands.get(brandId) || null : null;
}
export function flowEditableName(flow: SeedRow) {
  return flowNamePart(flow.name, flowBrand(flow));
}
function flowView(row: SeedRow): SeedRow {
  if (!flowViews.has(row)) flowViews.set(row, new Proxy(row, {
    get(target, key) {
      if (key === 'name') return brandedFlowName(target.name, flowBrand(target));
      return Reflect.get(target, key);
    },
  }));
  return flowViews.get(row)!;
}
function viewRows(table: TableName) { return table === 'flows' ? rows.flows.map(flowView) : rows[table]; }
export const db = new Proxy(rows, {
  get(target, property) {
    if (typeof property === 'string' && TABLES.includes(property as TableName)) void loadTable(property as TableName).catch((): void => undefined);
    return property === 'flows' ? viewRows('flows') : Reflect.get(target, property);
  }
}) as Record<TableName, SeedRow[]>;
export let ORG_ID = '';
export let CURRENT_USER_ID = '';
export let persistenceError = '';
const pendingWrites = new WeakMap<SeedRow, Promise<SeedRow>>();
export const whenPersisted = (row: SeedRow): Promise<SeedRow> => pendingWrites.get(row) || Promise.resolve(row);

async function request(table: TableName, method: 'POST' | 'PATCH', row: SeedRow) {
  const response = await fetch(`/api/v1/ui/${table}`, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(table === 'flows' ? { ...row, name: flowEditableName(row) } : row) });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    persistenceError = body.error || `Cannot persist ${table} (${response.status})`;
    window.dispatchEvent(new CustomEvent('cf:persistence-error', { detail: persistenceError }));
    throw new Error(persistenceError);
  }
  persistenceError = '';
  return body as SeedRow;
}

export async function load() {
  const sessionResponse = await fetch('/api/session');
  if (!sessionResponse.ok) throw new Error(`Cannot load session (${sessionResponse.status})`);
  const session = await sessionResponse.json();
  CURRENT_USER_ID = session.user.id;
  ORG_ID = session.organizations[0]?.id || '';
  rows.organizations = session.organizations;
  index.organizations = new Map(rows.organizations.map(row => [row.id, row]));
  loaded.add('organizations');
  await Promise.all([loadTable('users'), loadTable('favorites'), loadTable('recents')]);
}
export const byId = (table: TableName, id: string): SeedRow | null => {
  void loadTable(table).catch((): void => undefined);
  const row = index[table]?.get(id);
  if (!row && id && PAGED.has(table)) readRow(table, id);
  return row ? (table === 'flows' ? flowView(row) : row) : null;
};
/** A cached row without loading anything. */
export const cachedRow = (table: TableName, id: string): SeedRow | null => index[table]?.get(id) || null;
/** Adds rows from a paged read to the cache, keeping one object per ID so edits show everywhere. */
export function upsertRows(table: TableName, incoming: SeedRow[]): SeedRow[] {
  return incoming.map(row => {
    const known = row.id ? index[table].get(row.id) : undefined;
    if (known) return Object.assign(known, row);
    rows[table].push(row);
    if (row.id) index[table].set(row.id, row);
    return row;
  });
}
/** True while a paged table is still asking the server for this row. */
export const isReadingRow = (table: TableName, id: string) => !!rowReads.get(table + ':' + id);
function readRow(table: TableName, id: string) {
  const key = table + ':' + id;
  if (rowReads.has(key)) return;
  const read = (async () => {
    try {
      const response = await fetch(`/api/v1/ui/${table}?id=${encodeURIComponent(id)}`);
      if (!response.ok) throw new Error('Cannot load ' + table + ' (' + response.status + ')');
      const value: unknown = await response.json();
      if (Array.isArray(value)) upsertRows(table, (value as SeedRow[]).filter(row => row.id === id));
    } catch (error: any) {
      window.dispatchEvent(new CustomEvent('cf:data-error', { detail: error.message }));
    } finally {
      // A missing row is not requested again until the table is refreshed.
      rowReads.set(key, null);
      window.dispatchEvent(new CustomEvent('cf:data-updated', { detail: table }));
    }
  })();
  rowReads.set(key, read);
}
/** Lets byId ask the server again for rows of a paged table that it could not find. */
export function forgetRowReads(table: TableName) {
  for (const key of [...rowReads.keys()]) if (key.startsWith(table + ':') && !rowReads.get(key)) rowReads.delete(key);
}
export const where = (table: TableName, predicate: (row: SeedRow) => boolean): SeedRow[] => { void loadTable(table).catch((): void => undefined); return viewRows(table).filter(predicate); };
export const eq = (table: TableName, field: string, value: unknown): SeedRow[] => { void loadTable(table).catch((): void => undefined); return viewRows(table).filter(row => row[field] === value); };
function acceptSaved(table: TableName, row: SeedRow, saved: SeedRow): SeedRow {
  if (row.id && row.id !== saved.id) index[table].delete(row.id);
  Object.assign(row, saved);
  if (row.id) index[table].set(row.id, row);
  return row;
}
export function insert(table: TableName, row: SeedRow) {
  rows[table].unshift(row);
  if (row.id) index[table].set(row.id, row);
  const write = (async () => {
    try {
      return acceptSaved(table, row, await request(table, 'POST', row));
    } catch (error) {
      rows[table] = rows[table].filter(candidate => candidate !== row);
      if (row.id) index[table].delete(row.id);
      throw error;
    }
  })();
  pendingWrites.set(row, write);
  void write.catch((): void => undefined);
  return row;
}
export function update(table: TableName, id: string, patch: Partial<SeedRow>) {
  const row = byId(table, id);
  if (row) {
    Object.assign(row, patch);
    const write = request(table, 'PATCH', row).then((saved): SeedRow => { return acceptSaved(table, row, saved); });
    pendingWrites.set(row, write);
    void write.catch((): void => undefined);
  }
  return row;
}
export function removeLocal(table: TableName, id: string) {
  rows[table] = rows[table].filter(row => row.id !== id);
  index[table].delete(id);
}
export function sortBy<T>(rows: T[], key: string | ((row: T) => any), direction = 'desc'): T[] {
  return [...rows].sort((a, b) => {
    const value = (row: T) => typeof key === 'function' ? key(row) : (row as Record<string, any>)[key];
    const va = value(a), vb = value(b);
    if (va == null) return 1;
    if (vb == null) return -1;
    return (va > vb ? 1 : va < vb ? -1 : 0) * (direction === 'desc' ? -1 : 1);
  });
}
export const uniq = <T>(items: T[]): T[] => [...new Set(items)];
